// EL SELECTOR DE FUENTES DE LA CAJA DE LA HOME —
// ADR-2026-09-21-la-caja-de-la-home-abre-el-selector-de-fuentes.
//
// La card "Agregar producto" que vive a la izquierda de la caja del Agente abre ESTE
// pop-up: el MISMO que usan los slots de escena del Estudio de video
// (`SelectorDeImagenModal`), con una pestaña por fuente y las miniaturas adentro. En la
// home no hay biblioteca lateral —esa columna es de la pantalla dedicada y necesita
// ancho— y las cards del "+" abren un selector distinto por tipo: hay que saber de
// antemano si lo que uno quiere es "un producto" o "una imagen ya generada" para
// encontrarlo. Con las fuentes en pestañas se entra por cualquiera y se cambia sin
// cerrar nada.
//
// Los datos son los de la biblioteca (`useFuentesDeAdjuntos`): las mismas seis fuentes,
// la misma carga perezosa y los mismos ids de adjunto, así lo que se engancha acá se
// reconoce en el chat.
//
// Las pestañas además DAN DE ALTA: cada grilla arranca con las tarjetas de alta de SU
// fuente —subir un producto, crear un avatar o subir su foto, subir una pose, subir un
// fondo— y el alta ocupa el cuerpo de esta misma ventana
// (ADR-2026-09-22-el-alta-de-producto-adentro-del-selector-de-fuentes y
// ADR-2026-09-22-las-altas-de-todas-las-fuentes-en-el-selector).
//
// Qué alta tiene cada fuente sale de `ALTAS_POR_PESTANA` y las pantallas son
// `AltaDeBiblioteca`, las MISMAS que muestra el panel lateral del Agente —que a su vez
// monta las de los modos (`SubirProductoInline`, `CreateView`/`UploadFlow` del picker de
// modelos, `ExplicadorSubirPose`, `FondoUploadView`)—, así que no hay una segunda versión
// de ninguna ni dos vocabularios para lo mismo.
//
// Lo que se da de alta vuelve distinto según la fuente, copiando lo que hace la pantalla
// de origen: un modelo o un fondo quedan ENGANCHADOS y el pop-up se cierra (es lo que
// hacen su "Utilizar" y su "Confirmar"); un producto o una pose vuelven a la grilla
// refrescada, para engancharlos de un toque. La pose, además, se procesa mientras tanto:
// la tarjeta lo dice y no se puede volver a tocar hasta que termine.
//
// Elegir ADJUNTA Y CIERRA, como el selector del Estudio y como los selectores del "+":
// el pop-up tapa la home entera, y quedarse abierto después de enganchar deja al usuario
// sin ver el chip que acaba de sumar a la caja. Para enganchar varias cosas de una está
// la biblioteca de la pantalla del Agente, que no tapa nada.

import { useIndiceDeBusqueda } from '../../lib/indiceDeBusquedaDeProductos';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckCheck,
  ChevronLeft,
  Image as ImageIcon,
  ImagePlus,
  Package,
  Palette,
  PersonStanding,
  Search,
  Shapes,
  Sparkles,
  Upload,
  User,
} from 'lucide-react';
import { LogoDePinterest } from './LogoDePinterest';
import { usePestanaDePinterest } from './usePestanaDePinterest';
import SelectorDeImagenModal, {
  DURACION_SALIDA_MS,
  type AccionDelSelector,
  type PestanaDelSelector,
} from '../../components/SelectorDeImagenModal';
import { useChatAtlas } from '../ChatAtlasContext';
import { useEntornoChatAtlas } from '../entornoContext';
import {
  ACEPTA_ADJUNTOS,
  archivosEnganchados,
  esArchivoDelUsuario,
  esLoteDeFotos,
  lugarLibreAdjuntos,
  MAX_FOTOS_LOTE,
  mensajeLimiteAdjuntos,
} from '../adjuntosChatAtlas';
import { prepararAdjunto } from './prepararAdjunto';
import {
  crearCarpetaDeElementos,
  eliminarCarpetaDeElementos,
  esArchivoDeFuente,
  fetchCarpetasDeElementos,
  fetchElementos,
  firmarOriginal,
  pintarCarpetaDeElementos,
  subirMuestraDeTipografia,
  renombrarCarpetaDeElementos,
  subirElemento,
  type CarpetaDeElementos,
  type ElementoDeMarca,
} from '../../pages/v2/elementosDeMarcaApi';
import { conElementosIniciales } from '../../pages/v2/elementosIniciales';
import { UNIDAD_ELEMENTOS } from '../../components/MiMarcaElementosTab';
import VacioDeElementos, { CARPETAS_SUGERIDAS } from '../../components/elementosDeMarca/VacioDeElementos';
import { usePrepararElementos } from '../../components/elementosDeMarca/PrepararElemento';
import { esZip } from '../../utils/fuentesDeUnZip';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import {
  adjuntoDeCarpetaDeMoodboard,
  adjuntoDeFondoGuardado,
  adjuntoDeModelo,
  ALTAS_POR_PESTANA,
  idDeCarpetaDeMoodboard,
  PESTANAS_BIBLIOTECA,
  adjuntoDeElementoDeMarca,
  adjuntoDeTipografia,
  idDeElementoDeMarca,
  idDeTipografia,
  pestanaVisible,
  type AltaDeBiblioteca as IdDeAlta,
  type PestanaBiblioteca,
} from './bibliotecaDeAdjuntos';
import { alternarFoto, alternarTodas, chipDeCarpeta, estanTodas, fotosMarcadas } from './seleccionDeMoodboard';
import {
  cambiarColorDeMoodboard,
  crearMoodboard,
  eliminarMoodboard,
  fetchFotos,
  fetchMoodboards,
  FOTOS_EN_PORTADA,
  renombrarMoodboard,
  subirFoto,
  type Moodboard,
  type MoodboardFoto,
} from '../../pages/v2/moodboardsApi';
import GrillaDeCarpetas from '../../components/moodboards/GrillaDeCarpetas';
import VacioDeEstilos from '../../components/moodboards/VacioDeEstilos';
import { fotosDelMoodboard } from '../../pages/v2/etiquetaDeAdjunto';
import { rubroVeMoodboards } from '../../lib/rubro';
import { useFuentesDeAdjuntos } from './useFuentesDeAdjuntos';
import { useAperturaAnimada } from '../../hooks/useAperturaAnimada';
import { fichasVisibles, tieneNombres } from './fichasDelSelector';
import { MiniaturasElegidas, type MiniaturaElegida } from './MiniaturasElegidas';
import { miniaturaDeAdjunto } from '../../pages/v2/etiquetaDeAdjunto';
import { SelectorDeOrden } from '../../pages/v2/components/ProductCatalogToolbar';
import {
  DEFAULT_PRODUCT_SORT,
  type ProductCatalogSort,
} from '../../pages/v2/productCatalogFilter';
import { usePalabraDeModelo } from '../../lib/nombreDeModelo';
import { AltaDeBiblioteca, type AltaConPantalla } from './AltaDeBiblioteca';
import { AgregarVariosModal } from '../../components/AgregarVariosModal';
import { TarjetaDeSubidaEnGrilla } from '../../components/TarjetaDeSubidaEnGrilla';
import { ModalDeSubirProductos, type ModoDeSubida } from '../../components/ModalDeSubirProductos';
import { uploadCustomPose } from '../../pages/v2/posesCustomApi';
import {
  FiguraEnPose,
  RetratoCreadoConIA,
  RetratoDePersona,
} from '../../components/dibujosDeAlta';

/**
 * Las pestañas, en el orden en que se ven, con lo que este pop-up agrega: el ÍCONO de la
 * pastilla y el aspecto de las miniaturas. La etiqueta y el vacío NO están acá: salen de
 * `PESTANAS_BIBLIOTECA`, que es la tabla de la biblioteca, para que las dos superficies
 * llamen igual a cada fuente —incluida la que cambia con el rubro ("Modelos" en Moda,
 * "Avatares" en el resto — ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
 *
 * Estilos (`moodboards`) no es una fuente como las demás: tiene dos niveles (carpetas y,
 * abierta una, sus fotos) y lo que agrega es UN chip por carpeta con las fotos marcadas
 * (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes). Va SEGUNDA, después de "Subir
 * archivo", que es con la que abre "Añadir referencia"
 * (ADR-2026-10-19-anadir-referencia-abre-en-subir-archivo).
 *
 * "Subidas" va primera y es la única que además de elegir AGREGA: su grilla arranca con
 * la tarjeta de "Subir imagen" y detrás está el historial de lo que ya se subió al chat,
 * que es lo que evita subir dos veces la misma foto. El rótulo de SU pastilla es el único
 * que se pisa ("Subir imagen" en vez de "Subidas"): es lo que la pestaña ofrece hacer.
 */
export type IdDeFuente = PestanaBiblioteca;

const PESTANAS: {
  id: PestanaBiblioteca;
  Icon: typeof Package;
  /** Pisa la etiqueta de la biblioteca. Sólo "Subidas" la usa. */
  etiqueta?: string;
  /** Fotos de cuerpo entero: a cuadrado se les corta la cabeza o los pies. */
  retrato?: boolean;
}[] = [
  // "Subir archivo" y no "Subir imagen": desde que el "+" del Agente abre este pop-up
  // (ADR-2026-09-25-el-mas-del-agente-abre-el-selector-de-fuentes), es la ÚNICA puerta
  // para adjuntar un PDF, una planilla, un texto o un video.
  { id: 'subidas', Icon: Upload, etiqueta: 'Subir archivo' },
  { id: 'moodboards', Icon: Palette },
  // Logos, elementos gráficos y tipografías, en carpetas (ADR-2026-10-19-elementos-de-marca).
  { id: 'elementos', Icon: Shapes },
  // Lo que la persona guardó en su Pinterest, donde el backend lo habilita
  // (ADR-2026-10-23-pinterest-guardados-como-referencia).
  { id: 'pinterest', Icon: LogoDePinterest },
  { id: 'productos', Icon: Package },
  { id: 'modelos', Icon: User, retrato: true },
  { id: 'poses', Icon: PersonStanding, retrato: true },
  { id: 'fondos', Icon: ImageIcon },
  { id: 'creaciones', Icon: Sparkles },
];

/** Cómo la llama la biblioteca (y, para modelos, cómo la llama ESTA marca). */
const deLaBiblioteca = (id: PestanaBiblioteca) =>
  PESTANAS_BIBLIOTECA.find((p) => p.id === id) ?? PESTANAS_BIBLIOTECA[0];

/**
 * Qué dice el botón que agrega lo marcado: "Agregar producto", "Agregar 3 poses"… y, si
 * hay de varias fuentes, "Agregar 4 elementos". Las palabras son las de cada fuente; la
 * de modelos sigue al rubro ("modelo" / "avatar"), como su pestaña.
 */
const UNIDADES: Record<PestanaBiblioteca, [string, string]> = {
  subidas: ['referencia', 'referencias'],
  productos: ['producto', 'productos'],
  modelos: ['modelo', 'modelos'],
  poses: ['pose', 'poses'],
  fondos: ['fondo', 'fondos'],
  creaciones: ['creación', 'creaciones'],
  moodboards: ['estilo', 'estilos'],
  elementos: ['elemento de marca', 'elementos de marca'],
  pinterest: ['imagen de Pinterest', 'imágenes de Pinterest'],
};
export function rotuloDeAgregar(pestanas: readonly PestanaBiblioteca[], palabraDeModelo?: { singular: string; plural: string }): string {
  const n = pestanas.length;
  if (n === 0) return 'Agregar';
  const unica = pestanas.every((p) => p === pestanas[0]) ? pestanas[0]! : null;
  if (!unica) return `Agregar ${n} elementos`;
  const [uno, varios] =
    unica === 'modelos' && palabraDeModelo
      ? [palabraDeModelo.singular.toLowerCase(), palabraDeModelo.plural.toLowerCase()]
      : UNIDADES[unica];
  return n === 1 ? `Agregar ${uno}` : `Agregar ${n} ${varios}`;
}

/** Con qué pestaña abre. Es la que promete la card que lo abre ("Agregar imagen"). */
const PESTANA_INICIAL: IdDeFuente = 'productos';

/** Cuántas miniaturas se dibujan por página: seis filas de cinco. */
const PAGINA_DE_GRILLA = 30;

/**
 * El ícono de cada tarjeta de alta. La etiqueta NO está acá: sale de `ALTAS_POR_PESTANA`,
 * la misma tabla que usa la biblioteca, para que las dos superficies llamen igual a cada
 * alta (incluida la que cambia con el rubro: "Crear personaje" / "Crear avatar").
 *
 * Crear un modelo es lo único generativo —la IA lo inventa a partir de una descripción— y
 * por eso va con las chispas y el tono de marca; el resto sube una foto.
 *
 * `dibujo` es lo que va en la foto del frente de la tarjeta: las de personas y poses se
 * reconocen por el dibujo (ADR-2026-10-09-las-altas-de-modelos-y-poses-se-reconocen-por-el-dibujo);
 * el resto queda con el ícono de imagen.
 */
const ICONO_DE_ALTA: Record<IdDeAlta, { Icon: typeof Upload; tono?: 'ia'; dibujo?: React.ReactNode }> = {
  'subir-producto': { Icon: Upload },
  'crear-modelo': { Icon: Sparkles, tono: 'ia', dibujo: <RetratoCreadoConIA /> },
  'subir-modelo': { Icon: Upload, dibujo: <RetratoDePersona /> },
  'subir-pose': { Icon: Upload, dibujo: <FiguraEnPose /> },
  'subir-fondo': { Icon: Upload },
  'subir-imagen': { Icon: Upload },
};

export const ModalDeFuentes: React.FC<{
  abierto: boolean;
  onCerrar: () => void;
  /**
   * Otro destino para lo elegido o subido, en vez del chat: el panel "Editar clip" del
   * Estudio lo usa para su imagen de referencia. Con esto no se toca el chat (ni su tope
   * de adjuntos ni su lote de fotos), no se esconde lo que ya está en él y se sube UNA
   * imagen. Sin esto, el selector de siempre
   * (ADR-2026-09-27-editar-clip-agrega-con-el-selector-del-agente).
   */
  onAdjuntar?: (adjunto: ChatAttachment) => void;
  /**
   * Con qué pestaña abre, cuando quien lo abre ya sabe qué viene a buscar: el modo Redes
   * tiene tres botones —Añadir referencia, Producto, Modelo— y cada uno abre el MISMO
   * selector en su pestaña (`moodboards`, `productos`, `modelos`). Sin esto, "Productos",
   * como siempre. Estilos (`moodboards`) donde no se ofrece abre en "Subir archivo".
   */
  pestanaInicial?: IdDeFuente;
  /**
   * Con `onAdjuntar`, si se pueden elegir VARIOS. Por defecto no: "Editar clip" quiere una
   * sola imagen y sigue como estaba (un toque elige y cierra). El modo Redes lo pide. Sin
   * `onAdjuntar` (el chat) siempre se eligen varios
   * (ADR-2026-10-04-el-selector-de-fuentes-elige-varios).
   */
  multiple?: boolean;
  /**
   * Con `onAdjuntar` y `multiple`: lo que el destino YA tiene agregado, y cómo sacarlo. Con
   * esto el pop-up abre con eso marcado, desmarcarlo lo quita al confirmar y el botón dice
   * "Listo" cuando no hay nada nuevo que agregar
   * (ADR-2026-10-05-el-pie-del-selector-muestra-lo-elegido). En el chat salen solos.
   */
  adjuntosActuales?: ChatAttachment[];
  onQuitar?: (id: string) => void;
  /**
   * En Productos, en vez de la tarjeta "Subir producto" (que abre el alta adentro de esta
   * ventana), las DOS tarjetas de subida de los modos —"Subir un producto" y "Subir varios
   * productos"— arriba de la grilla, que abren el pop-up de subir encima
   * (ADR-2026-10-16-subir-en-dos-tarjetas-en-todos-los-modos). Desde
   * ADR-2026-10-20-subir-uno-o-varios-productos-en-todo-selector es el default en TODOS los
   * selectores (chat, cards del Asistente, Editar, el visor…): la tarjeta de siempre llevaba
   * al alta vieja y no ofrecía subir varios. `false` vuelve a la tarjeta de alta.
   */
  subidaEnDosTarjetas?: boolean;
  /**
   * Con `onAdjuntar` y `multiple`, si ofrece la pestaña Moodboards. Lo que agrega es una
   * CARPETA (un puntero que el agente mira con `ver_moodboard`), así que sólo sirve donde lo
   * elegido le llega al agente: el Asistente de Crear lo pide, porque su caja pasa al chat al
   * mandar. Una generación directa no sabe qué hacer con una carpeta. Sin `onAdjuntar` (el
   * chat) se ofrece siempre. En los dos casos, sólo si la marca ve moodboards
   * (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes).
   */
  conMoodboards?: boolean;
  /**
   * Con `onAdjuntar` y `multiple`, si ofrece la pestaña Pinterest. Un pin viaja como link de
   * `i.pinimg.com`, que sólo aceptan las generaciones de IMAGEN, la edición y el chat: el video,
   * Recrear y el Catálogo lo rebotarían. Sin `onAdjuntar` (el chat) se ofrece siempre. En los dos
   * casos, sólo si el backend la habilita (ADR-2026-10-26-pinterest-abrir-a-todos).
   */
  conPinterest?: boolean;
}> = ({ abierto, onCerrar, onAdjuntar, pestanaInicial = PESTANA_INICIAL, multiple = false, adjuntosActuales, onQuitar, subidaEnDosTarjetas = true, conMoodboards = false, conPinterest = false }) => {
  /** Tocar MARCA y se confirma con el botón del pie; si no, tocar agrega y cierra. */
  const eligeVarios = !onAdjuntar || multiple;
  const chat = useChatAtlas();
  const {
    usuarioId,
    companyId,
    brandId,
    marcaResuelta,
    rubro,
    plan,
    refrescarProductos,
    refrescarModelos,
  } = useEntornoChatAtlas();
  const ofreceMoodboards = rubroVeMoodboards(rubro) && (!onAdjuntar || (conMoodboards && multiple));
  /** Las pestañas de la tira: Estilos donde se ofrece, y Poses salvo en Hogar y Decoración
   *  (ADR-2026-10-19-hogar-y-decoracion-no-ve-poses). */
  /** Elementos de marca, en todos los rubros, donde se eligen varios (el chat y Crear):
   *  "Editar clip" quiere UNA imagen suya (ADR-2026-10-19-elementos-de-marca). */
  const ofreceElementos = !onAdjuntar || multiple;
  // Pinterest (ADR-2026-10-23-pinterest-guardados-como-referencia): en el chat y donde el destino
  // la pide (`conPinterest`), y sólo si el backend la habilita para esta persona. Su aviso va al del pop-up, que
  // se declara más abajo (de ahí la referencia).
  const avisoParaPinterestRef = useRef<(texto: string | null) => void>(() => {});
  const avisoParaPinterest = useCallback((texto: string | null) => avisoParaPinterestRef.current(texto), []);
  const puedeOfrecerPinterest = !onAdjuntar || (multiple && conPinterest);
  // Donde no se ofrece, ni se pregunta el estado al backend.
  const pinterest = usePestanaDePinterest({ abierto: abierto && puedeOfrecerPinterest, setAviso: avisoParaPinterest });
  const ofrecePinterest = puedeOfrecerPinterest && pinterest.disponible;
  const seOfrece = (id: PestanaBiblioteca) =>
    id === 'moodboards'
      ? ofreceMoodboards
      : id === 'elementos'
        ? ofreceElementos
        : id === 'pinterest'
          ? ofrecePinterest
          : pestanaVisible(id, rubro);
  // Entra y sale con transición: sigue montado mientras sale, con `visible` en `false`
  // (ADR-2026-09-25-el-selector-de-fuentes-entra-y-sale-con-transicion).
  const { montado, visible } = useAperturaAnimada(abierto, DURACION_SALIDA_MS);
  /** Con qué pestaña abre de verdad: Estilos sólo donde se ofrece; si no, "Subir archivo",
   *  que es lo otro que promete "Añadir referencia". */
  const inicial: PestanaBiblioteca = seOfrece(pestanaInicial) ? pestanaInicial : 'subidas';
  const [pestanaElegida, setPestana] = useState<PestanaBiblioteca>(inicial);
  // Si Estilos o Poses dejan de ofrecerse con la pestaña abierta (se cambió a una marca de
  // otro rubro), se ve "Subir archivo": una pestaña que no está en la tira dejaba el pop-up
  // sin grilla.
  const pestana: PestanaBiblioteca = seOfrece(pestanaElegida) ? pestanaElegida : 'subidas';
  const [aviso, setAviso] = useState<string | null>(null);
  avisoParaPinterestRef.current = setAviso;
  // Los tableros de Pinterest se piden recién al entrar a su pestaña.
  const entrarAPinterest = pinterest.entrar;
  useEffect(() => {
    if (pestana === 'pinterest') entrarAPinterest();
  }, [pestana, entrarAPinterest]);
  const [subiendo, setSubiendo] = useState(false);
  /**
   * Lo marcado para AGREGAR (lo nuevo), por el id de su adjunto, en el orden en que se
   * tocó. Guarda de qué pestaña salió: la selección sobrevive a cambiar de pestaña, a
   * buscar y a paginar, y el botón del pie dice qué se agrega.
   */
  const [elegidos, setElegidos] = useState<Map<string, { adjunto: ChatAttachment; pestana: PestanaBiblioteca }>>(
    () => new Map(),
  );
  /** Lo que el destino ya tenía y se DESMARCÓ: se quita al confirmar. */
  const [quitados, setQuitados] = useState<Set<string>>(() => new Set());
  /**
   * Qué alta está ocupando el cuerpo del pop-up (`null` = la grilla). Son las de
   * `ALTAS_POR_PESTANA` más `varios`, el alta en bloque de productos, que se abre DESDE
   * el alta de producto y también se dibuja acá adentro
   * (ADR-2026-09-22-el-alta-en-bloque-tambien-adentro-del-selector).
   */
  const [alta, setAlta] = useState<AltaConPantalla | 'varios' | null>(null);
  /** El pop-up de subir de las dos tarjetas (`subidaEnDosTarjetas`); `null` = cerrado. */
  const [subirEnVentana, setSubirEnVentana] = useState<ModoDeSubida | null>(null);
  /** El alta en bloque está guardando: la salida de ESTA ventana se bloquea, como se
   *  bloquea la del popup cuando corre por su cuenta. */
  const [variosOcupado, setVariosOcupado] = useState(false);
  /** La pose elegida se está procesando en el backend (tarda): la tarjeta lo dice. */
  const [subiendoPose, setSubiendoPose] = useState(false);
  // El buscador y el orden de la pestaña, como en la biblioteca
  // (ADR-2026-09-25-el-selector-de-fuentes-toma-la-biblioteca). El orden es sólo de
  // Productos y es el mismo de los pickers de los modos.
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState<ProductCatalogSort>(DEFAULT_PRODUCT_SORT);
  // Cambiar de pestaña limpia el buscador: es de la grilla que se va.
  useEffect(() => {
    setBusqueda('');
  }, [pestana]);
  // ── Moodboards: las carpetas, la abierta y las fotos de cada una que se abrió. Se piden al
  //    entrar a la pestaña, no antes. Acá también se ARMAN: carpeta nueva y fotos nuevas
  //    (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes-2).
  const [carpetas, setCarpetas] = useState<Moodboard[] | null>(null);
  const [carpetaAbierta, setCarpetaAbierta] = useState<Moodboard | null>(null);
  const [fotosPorCarpeta, setFotosPorCarpeta] = useState<Map<string, MoodboardFoto[]>>(() => new Map());
  /** Una tanda de fotos subiendo a una carpeta: cuántas faltan. Una tanda por vez. */
  const [subiendoAMoodboard, setSubiendoAMoodboard] = useState<{ carpetaId: string; quedan: number } | null>(null);
  /** Lo recién subido, para marcarlo (ver el efecto de abajo). */
  const [porMarcar, setPorMarcar] = useState<{ carpetaId: string; ids: string[] } | null>(null);
  const inputMoodboardRef = useRef<HTMLInputElement | null>(null);
  // ── Elementos de marca: igual que Estilos, carpetas y adentro sus archivos; acá se elige
  //    cada logo o elemento gráfico por separado (ADR-2026-10-19-elementos-de-marca).
  const [carpetasDeElementos, setCarpetasDeElementos] = useState<CarpetaDeElementos[] | null>(null);
  const [carpetaDeElementos, setCarpetaDeElementos] = useState<CarpetaDeElementos | null>(null);
  const [elementosPorCarpeta, setElementosPorCarpeta] = useState<Map<string, ElementoDeMarca[]>>(() => new Map());
  const [subiendoElementos, setSubiendoElementos] = useState<{ carpetaId: string; quedan: number } | null>(null);
  const inputElementosRef = useRef<HTMLInputElement | null>(null);
  /** Las muestras CHICAS de las tipografías para la grilla (data URL), por id del elemento. */
  const [previasDeFuentes, setPreviasDeFuentes] = useState<Map<string, string>>(() => new Map());
  /** La tipografía cuya hoja de muestra se está dibujando y subiendo al marcarla. */
  const [preparandoFuente, setPreparandoFuente] = useState<string | null>(null);
  useEffect(() => {
    if (!abierto || pestana !== 'moodboards' || carpetas !== null || !companyId || !marcaResuelta) return;
    let vivo = true;
    fetchMoodboards({ companyId, brandId })
      .then((lista) => {
        if (vivo) setCarpetas(lista);
      })
      .catch((e) => {
        console.error('[ModalDeFuentes] moodboards', e);
        if (!vivo) return;
        setCarpetas([]);
        setAviso('No pudimos cargar los estilos. Probá de nuevo.');
      });
    return () => {
      vivo = false;
    };
  }, [abierto, pestana, carpetas, companyId, brandId, marcaResuelta]);
  const abrirCarpeta = (carpeta: Moodboard) => {
    setAviso(null);
    setCarpetaAbierta(carpeta);
    if (fotosPorCarpeta.has(carpeta.id) || !companyId) return;
    fetchFotos({ companyId, moodboardId: carpeta.id })
      .then((lista) => setFotosPorCarpeta((prev) => new Map(prev).set(carpeta.id, lista)))
      .catch((e) => {
        // Un fallo NO se guarda como carpeta vacía: con eso, volver a tocarla no pedía nada y
        // "Probá de nuevo" no se podía cumplir. Se vuelve a las carpetas, y tocarla de nuevo
        // reintenta (ADR-2026-10-19-review-191-las-fotos-que-fallan-se-reintentan).
        console.error('[ModalDeFuentes] fotos del moodboard', e);
        setAviso('No pudimos cargar las fotos de esa carpeta. Tocala de nuevo para reintentar.');
        setCarpetaAbierta((abierta) => (abierta?.id === carpeta.id ? null : abierta));
      });
  };
  const avisarMoodboard = (e: unknown, quePasaba: string) => {
    console.error(`[ModalDeFuentes] ${quePasaba}`, e);
    setAviso(e instanceof Error && e.message ? e.message : `No pudimos ${quePasaba}. Probá de nuevo.`);
  };
  // Crear, renombrar, pintar y borrar: lo mismo que la biblioteca del Agente
  // (`BibliotecaMoodboards`), y el catálogo que el agente lleva en el prompt se vuelve a pedir.
  // La carpeta nueva se ABRE: lo que sigue es subirle fotos.
  const crearCarpeta = async (nombre: string) => {
    if (!companyId) return;
    try {
      const carpeta = await crearMoodboard({ companyId, brandId, nombre, userId: usuarioId });
      setCarpetas((prev) => (prev ? [carpeta, ...prev] : prev));
      setFotosPorCarpeta((prev) => new Map(prev).set(carpeta.id, []));
      setCarpetaAbierta(carpeta);
      chat.refrescarMoodboards();
    } catch (e) {
      avisarMoodboard(e, 'crear la carpeta');
    }
  };
  const renombrarCarpeta = async (id: string, nombre: string) => {
    const antes = carpetas;
    setCarpetas((prev) => prev && prev.map((c) => (c.id === id ? { ...c, nombre } : c)));
    // El chip elegido de esa carpeta lleva su nombre.
    setElegidos((prev) => {
      const clave = idDeCarpetaDeMoodboard(id);
      const e = prev.get(clave);
      if (!e || e.adjunto.kind !== 'moodboard') return prev;
      return new Map(prev).set(clave, { ...e, adjunto: { ...e.adjunto, name: nombre } });
    });
    try {
      await renombrarMoodboard(id, nombre);
      chat.refrescarMoodboards();
    } catch (e) {
      setCarpetas(antes);
      avisarMoodboard(e, 'guardar el nombre');
    }
  };
  const pintarCarpeta = async (id: string, color: string) => {
    const antes = carpetas;
    setCarpetas((prev) => prev && prev.map((c) => (c.id === id ? { ...c, color } : c)));
    try {
      await cambiarColorDeMoodboard(id, color);
    } catch (e) {
      setCarpetas(antes);
      avisarMoodboard(e, 'guardar el color');
    }
  };
  const eliminarCarpeta = async (id: string) => {
    if (!companyId) return;
    try {
      await eliminarMoodboard({ companyId, moodboardId: id });
      setCarpetas((prev) => prev && prev.filter((c) => c.id !== id));
      // Lo elegido de esa carpeta ya no existe: sale de lo marcado y, al confirmar, del destino.
      sacarDeLoMarcado(idDeCarpetaDeMoodboard(id));
      chat.refrescarMoodboards();
    } catch (e) {
      avisarMoodboard(e, 'eliminar la carpeta');
    }
  };
  // Subir fotos a una carpeta: de a una, como en Mi marca, y cada una entra a la grilla en
  // cuanto termina. Al final quedan MARCADAS: se subieron para usarlas.
  const subirFotosACarpeta = async (carpeta: Moodboard, archivos: File[]) => {
    if (!companyId || archivos.length === 0) return;
    setAviso(null);
    setSubiendoAMoodboard({ carpetaId: carpeta.id, quedan: archivos.length });
    const subidas: string[] = [];
    for (const file of archivos) {
      try {
        const foto = await subirFoto({ companyId, moodboardId: carpeta.id, file });
        subidas.push(foto.id);
        // Sólo si la carpeta ya estaba traída: si el pop-up se reabrió en el medio, se pide entera.
        setFotosPorCarpeta((prev) =>
          prev.has(carpeta.id) ? new Map(prev).set(carpeta.id, [foto, ...(prev.get(carpeta.id) ?? [])]) : prev,
        );
        setCarpetas(
          (prev) =>
            prev &&
            prev.map((c) =>
              c.id === carpeta.id
                ? { ...c, cantidadFotos: c.cantidadFotos + 1, portada: [foto.thumbUrl, ...c.portada].slice(0, FOTOS_EN_PORTADA) }
                : c,
            ),
        );
      } catch (e) {
        avisarMoodboard(e, 'subir la foto');
      } finally {
        setSubiendoAMoodboard((prev) => (prev && prev.quedan > 1 ? { ...prev, quedan: prev.quedan - 1 } : null));
      }
    }
    if (subidas.length > 0) {
      setPorMarcar({ carpetaId: carpeta.id, ids: subidas });
      chat.refrescarMoodboards();
    }
  };
  // Lo recién subido queda marcado. En un efecto y no al terminar de subir: mientras subía
  // pudo cambiar lo elegido, y acá se lee lo de ahora.
  useEffect(() => {
    if (!porMarcar) return;
    setPorMarcar(null);
    const carpeta = carpetas?.find((c) => c.id === porMarcar.carpetaId);
    const fotos = fotosPorCarpeta.get(porMarcar.carpetaId);
    if (!carpeta || !fotos) return;
    const marcadas = fotosMarcadas(chipDeLaCarpeta(carpeta.id), fotos);
    for (const id of porMarcar.ids) marcadas.add(id);
    fijarCarpeta(carpeta, fotos, marcadas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [porMarcar]);
  useEffect(() => {
    if (!abierto || pestana !== 'elementos' || carpetasDeElementos !== null || !companyId || !marcaResuelta) return;
    if (!brandId) {
      setCarpetasDeElementos([]);
      return;
    }
    let vivo = true;
    conElementosIniciales({ companyId, brandId }, () => fetchCarpetasDeElementos({ companyId, brandId }))
      .then((lista) => {
        if (vivo) setCarpetasDeElementos(lista);
      })
      .catch((e) => {
        console.error('[ModalDeFuentes] elementos de marca', e);
        if (!vivo) return;
        setCarpetasDeElementos([]);
        setAviso('No pudimos cargar los elementos de tu marca. Probá de nuevo.');
      });
    return () => {
      vivo = false;
    };
  }, [abierto, pestana, carpetasDeElementos, companyId, brandId, marcaResuelta]);
  // Las miniaturas de las tipografías de la carpeta abierta: su nombre escrito con ellas.
  // Se dibujan una vez por tipografía.
  useEffect(() => {
    if (!carpetaDeElementos) return;
    const fuentes = (elementosPorCarpeta.get(carpetaDeElementos.id) ?? []).filter(
      (el) => el.tipo === 'font' && el.url && !previasDeFuentes.has(el.id),
    );
    if (fuentes.length === 0) return;
    let vivo = true;
    void (async () => {
      // La miniatura es el NOMBRE escrito con la fuente, para reconocerla; al marcarla viaja
      // la hoja del abecedario (ADR-2026-10-20-la-miniatura-de-la-tipografia-es-su-nombre).
      const { dibujarNombreDeTipografia } = await import('../../utils/muestraDeTipografia');
      for (const el of fuentes) {
        try {
          const canvas = await dibujarNombreDeTipografia({ url: el.url, nombre: el.titulo, idUnico: el.id, ancho: 640, alto: 400 });
          if (!vivo) return;
          const dataUrl = canvas.toDataURL('image/png');
          setPreviasDeFuentes((prev) => new Map(prev).set(el.id, dataUrl));
        } catch (e) {
          console.warn('[ModalDeFuentes] muestra de la tipografía', e);
        }
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carpetaDeElementos, elementosPorCarpeta]);
  const abrirCarpetaDeElementos = (carpeta: CarpetaDeElementos) => {
    setAviso(null);
    setCarpetaDeElementos(carpeta);
    if (elementosPorCarpeta.has(carpeta.id)) return;
    fetchElementos({ carpetaId: carpeta.id })
      .then((lista) => setElementosPorCarpeta((prev) => new Map(prev).set(carpeta.id, lista)))
      .catch((e) => {
        // Como en Estilos: el fallo no se guarda como carpeta vacía, así tocarla reintenta.
        console.error('[ModalDeFuentes] elementos de la carpeta', e);
        setAviso('No pudimos cargar esa carpeta. Tocala de nuevo para reintentar.');
        setCarpetaDeElementos((abierta) => (abierta?.id === carpeta.id ? null : abierta));
      });
  };
  const nuevaCarpetaDeElementos = async (nombre: string, color?: string) => {
    if (!companyId || !brandId) return null;
    const carpeta = await crearCarpetaDeElementos({ companyId, brandId, nombre, color, userId: usuarioId });
    setCarpetasDeElementos((prev) => (prev ? [carpeta, ...prev] : [carpeta]));
    setElementosPorCarpeta((prev) => new Map(prev).set(carpeta.id, []));
    return carpeta;
  };
  /** Marca o desmarca un logo o elemento gráfico. El original se firma recién al marcarlo:
   *  es lo que viaja al agente y a la generación, y la grilla no lo necesita. */
  const alternarElemento = async (el: ElementoDeMarca) => {
    const id = idDeElementoDeMarca(el.id);
    if (estaMarcado(id)) {
      alternar(adjuntoDeElementoDeMarca({ id: el.id, nombre: el.titulo, imageUrl: '' }), 'elementos');
      return;
    }
    const url = await firmarOriginal(el.path);
    if (!url) {
      setAviso('No pudimos abrir ese elemento. Probá de nuevo.');
      return;
    }
    alternar(adjuntoDeElementoDeMarca({ id: el.id, nombre: el.titulo, imageUrl: url }), 'elementos');
  };
  /** Marca o desmarca una tipografía: viaja su hoja de muestra, que se dibuja y se sube recién
   *  al marcarla (ADR-2026-10-19-la-tipografia-viaja-como-hoja-de-muestra). */
  const alternarTipografia = async (el: ElementoDeMarca) => {
    const id = idDeTipografia(el.id);
    if (estaMarcado(id)) {
      alternar(adjuntoDeTipografia({ id: el.id, nombre: el.titulo, imageUrl: '' }), 'elementos');
      return;
    }
    if (preparandoFuente) return;
    setPreparandoFuente(el.id);
    try {
      const url = await subirMuestraDeTipografia(el);
      alternar(adjuntoDeTipografia({ id: el.id, nombre: el.titulo, imageUrl: url }), 'elementos');
    } catch (e) {
      avisarMoodboard(e, 'preparar la tipografía');
    } finally {
      setPreparandoFuente(null);
    }
  };
  /** Subir a la carpeta abierta: de a uno, y cada uno entra a la grilla al terminar. */
  const subirAElementos = async (carpeta: CarpetaDeElementos, archivos: File[]) => {
    if (!companyId || !brandId || archivos.length === 0) return;
    setAviso(null);
    setSubiendoElementos({ carpetaId: carpeta.id, quedan: archivos.length });
    for (const file of archivos) {
      try {
        const el = await subirElemento({ companyId, brandId, carpeta, file });
        setElementosPorCarpeta((prev) =>
          prev.has(carpeta.id) ? new Map(prev).set(carpeta.id, [el, ...(prev.get(carpeta.id) ?? [])]) : prev,
        );
        setCarpetasDeElementos(
          (prev) =>
            prev &&
            prev.map((c) =>
              c.id === carpeta.id
                ? {
                    ...c,
                    cantidadFotos: c.cantidadFotos + 1,
                    portada: el.tipo === 'font' ? c.portada : [el.url, ...c.portada].slice(0, FOTOS_EN_PORTADA),
                  }
                : c,
            ),
        );
      } catch (e) {
        avisarMoodboard(e, `subir «${file.name}»`);
      } finally {
        setSubiendoElementos((prev) => (prev && prev.quedan > 1 ? { ...prev, quedan: prev.quedan - 1 } : null));
      }
    }
  };
  // Cada imagen pasa antes por la preparación (recortar, título), como en Mi marca
  // (ADR-2026-10-20-el-elemento-se-prepara-antes-de-subir). La carpeta es la abierta al
  // elegir: la pantalla de preparación tapa todo, así que no cambia en el medio.
  const carpetaAlElegirRef = useRef<CarpetaDeElementos | null>(null);
  const { preparar: prepararElementos, pantalla: pantallaDePreparar } = usePrepararElementos((files) => {
    const carpeta = carpetaAlElegirRef.current;
    if (carpeta) void subirAElementos(carpeta, files);
  }, setAviso);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const {
    fichas,
    cargando,
    refrescarSubidas,
    refrescarPosesPropias,
    refrescarFondosGuardados,
    hayMas,
    pedirMas,
  } = useFuentesDeAdjuntos(pestana);
  // Cuántas miniaturas se DIBUJAN: de a `PAGINA_DE_GRILLA`, sumando otra página cuando
  // el centinela del pie queda a la vista. Primero lo que ya está traído; agotado eso,
  // se le pide otra página a la base (`pedirMas`, sólo Creaciones y Subidas). Cambiar de
  // pestaña o de búsqueda vuelve a la primera página
  // (ADR-2026-09-25-el-selector-de-fuentes-carga-miniaturas-y-por-scroll).
  const [dibujadas, setDibujadas] = useState(PAGINA_DE_GRILLA);
  useEffect(() => {
    setDibujadas(PAGINA_DE_GRILLA);
  }, [pestana, busqueda, orden]);
  // La palabra del rubro ("Modelo" / "Avatar") la resuelven los `get` de
  // `PESTANAS_BIBLIOTECA`, que leen el cache. El hook se llama igual porque es lo que
  // vuelve a dibujar el pop-up cuando se cambia de marca con él abierto: sin esto, la
  // pastilla se quedaría con la palabra de la marca anterior.
  const palabraDeModelo = usePalabraDeModelo();

  // Enganchar es lo MISMO que en la biblioteca: una creación o una subida es un ARCHIVO
  // del mensaje y cuenta para el tope del "+"; un producto, un modelo o una referencia
  // del catálogo, no.
  const enganchar = (adjunto: ChatAttachment): boolean => {
    if (onAdjuntar) {
      onAdjuntar(adjunto);
      setAviso(null);
      return true;
    }
    if (
      esArchivoDelUsuario(adjunto) &&
      lugarLibreAdjuntos(archivosEnganchados(chat.adjuntos)) === 0
    ) {
      setAviso(mensajeLimiteAdjuntos());
      return false;
    }
    chat.agregarAdjunto(adjunto);
    setAviso(null);
    return true;
  };

  const cerrar = () => {
    // Guardando en bloque no se cierra: las subidas quedarían en vuelo sin nadie que las
    // termine. Es el mismo bloqueo que hace el popup con su X.
    if (variosOcupado) return;
    // Con el pop-up de subir abierto encima, el Escape lo cierra a él y no a esta ventana
    // (los dos escuchan la tecla).
    if (subirEnVentana) return;
    onCerrar();
  };
  // Lo que se resetea al cerrar (la pestaña, el alta, el aviso) se resetea al VOLVER a
  // abrir, no al cerrar: hacerlo al tocar cerrar cambiaba la grilla mientras la hoja
  // todavía bajaba. Y no al desmontar, como estuvo: si se reabre durante los 200 ms de
  // salida el pop-up nunca se desmonta, y reaparecía en el alta o con el aviso viejo
  // (ADR-2026-09-25-revision-greptile-el-selector-se-reabre-limpio). Al abrir, el primer
  // frame se dibuja con el estilo de cerrado, así que el cambio no se ve.
  const abiertoAntesRef = useRef(abierto);
  useEffect(() => {
    const seAbrio = abierto && !abiertoAntesRef.current;
    abiertoAntesRef.current = abierto;
    if (!seAbrio) return;
    setAviso(null);
    setPestana(inicial);
    setAlta(null);
    setSubirEnVentana(null);
    setElegidos(new Map());
    setQuitados(new Set());
    // También el buscador: el efecto que lo limpia corre al cambiar de pestaña, y si
    // Productos ya era la abierta, el texto viejo seguía filtrando la grilla.
    setBusqueda('');
    // Los moodboards se vuelven a pedir: en Mi marca pudieron sumar fotos o carpetas.
    setCarpetas(null);
    setCarpetaAbierta(null);
    setFotosPorCarpeta(new Map());
    setPorMarcar(null);
    setCarpetasDeElementos(null);
    setCarpetaDeElementos(null);
    setElementosPorCarpeta(new Map());
  }, [abierto, inicial]);

  // Subir una pose propia: lo MISMO que hace el panel de la biblioteca —el backend arma
  // el muñeco en esa pose y tarda—, así que el pop-up vuelve a la grilla enseguida y la
  // tarjeta de alta avisa que está procesando. Al terminar, la pose ya está en "Mis
  // poses" (primera de la grilla).
  const subirPose = async (archivo: File) => {
    if (!companyId || !usuarioId) {
      setAviso('No pudimos identificar tu cuenta. Probá de nuevo en un momento.');
      return;
    }
    setSubiendoPose(true);
    setAviso(null);
    try {
      await uploadCustomPose({
        userId: usuarioId,
        idCompany: companyId,
        file: archivo,
        cantidadPersonas: 1,
      });
      await refrescarPosesPropias();
    } catch (e) {
      console.error('[ModalDeFuentes] subir pose', e);
      setAviso('No pudimos procesar la pose. Probá de nuevo con otra foto.');
    } finally {
      setSubiendoPose(false);
    }
  };

  // Subir imágenes: el mismo camino que el "+" de la caja (`prepararAdjunto`, que las
  // sube a `agent-refs`), con el mismo tope y los mismos mensajes. Se cierra al terminar
  // —lo subido ya es un chip en la caja— y se refresca "Subidas" para la próxima vez.
  const subirImagenes = async (archivos: File[]) => {
    if (archivos.length === 0) return;

    // Con otro destino, sin lote ni tope del chat: una sola imagen, o todas las elegidas
    // si ese destino acepta varias (`multiple`).
    if (onAdjuntar) {
      setSubiendo(true);
      let fallo: string | null = null;
      let subidas = 0;
      for (const archivo of multiple ? archivos : archivos.slice(0, 1)) {
        try {
          onAdjuntar(await prepararAdjunto(archivo, companyId));
          subidas += 1;
        } catch (err) {
          fallo = fallo ?? (err instanceof Error ? err.message : 'No pude leer ese archivo.');
        }
      }
      setSubiendo(false);
      if (subidas > 0) void refrescarSubidas();
      if (fallo) setAviso(fallo);
      else cerrar();
      return;
    }

    // MUCHAS fotos de una (`UMBRAL_LOTE_FOTOS` o más) no son referencias de una imagen:
    // son productos por cargar, y entran como LOTE —un chip, sin viajar al modelo—
    // (ADR-2026-09-08-el-chat-trabaja-en-lote). Es el mismo reparto que hacía "Agregar
    // archivos o fotos" en el menú del "+", y se copia acá porque en la home ese menú ya
    // no está (ADR-2026-09-21-la-caja-de-la-home-no-lleva-el-mas): sin esto, subir 20
    // fotos desde el pop-up las metía de a una hasta llenar el tope de adjuntos y perdía
    // la capacidad de cargar productos en tanda.
    if (esLoteDeFotos(archivos)) {
      chat.agregarLoteFotos(archivos.slice(0, MAX_FOTOS_LOTE));
      cerrar();
      return;
    }

    const libre = lugarLibreAdjuntos(archivosEnganchados(chat.adjuntos));
    if (libre === 0) {
      setAviso(mensajeLimiteAdjuntos());
      return;
    }
    setSubiendo(true);
    let fallo: string | null = null;
    for (const archivo of archivos.slice(0, libre)) {
      try {
        chat.agregarAdjunto(await prepararAdjunto(archivo, companyId));
      } catch (err) {
        // Se guarda el PRIMER motivo y se sigue con el resto, como en la caja.
        fallo = fallo ?? (err instanceof Error ? err.message : 'No pude leer ese archivo.');
      }
    }
    setSubiendo(false);
    void refrescarSubidas();
    if (fallo) {
      setAviso(fallo);
      return;
    }
    cerrar();
  };

  // Lo que el destino ya tiene: los adjuntos del chat, o los que pasó quien lo abrió.
  // Sólo lo que este pop-up ofrece (fotos, productos, modelos, referencias, videos y, si
  // está su pestaña, carpetas de moodboard): un PDF o una planilla enganchados en el chat
  // no son de acá y no se tocan.
  const existentes: ChatAttachment[] = eligeVarios
    ? (onAdjuntar ? (adjuntosActuales ?? []) : chat.adjuntos).filter(
        (a) =>
          a.kind === 'image' ||
          a.kind === 'product' ||
          a.kind === 'model' ||
          a.kind === 'referencia' ||
          a.kind === 'video' ||
          (ofreceMoodboards && a.kind === 'moodboard'),
      )
    : [];
  const quitarDelDestino = (id: string) => (onAdjuntar ? onQuitar?.(id) : chat.quitarAdjunto(id));
  /** Marcado = elegido ahora, o ya agregado y no desmarcado. */
  const estaMarcado = (id: string): boolean =>
    elegidos.has(id) || (existentes.some((a) => a.id === id) && !quitados.has(id));
  /** Marca o desmarca: lo que ya estaba se anota para quitar; lo nuevo, para agregar. */
  const alternar = (adjunto: ChatAttachment, deQuePestana: PestanaBiblioteca) => {
    if (existentes.some((a) => a.id === adjunto.id)) {
      setQuitados((prev) => {
        const next = new Set(prev);
        if (next.has(adjunto.id)) next.delete(adjunto.id);
        else next.add(adjunto.id);
        return next;
      });
      return;
    }
    setElegidos((prev) => {
      const next = new Map(prev);
      if (next.has(adjunto.id)) next.delete(adjunto.id);
      else next.set(adjunto.id, { adjunto, pestana: deQuePestana });
      return next;
    });
  };
  /** Saca algo de lo marcado (la X de su miniatura en el pie): lo nuevo deja de agregarse y
   *  lo que ya estaba se quita al confirmar. Una carpeta que ya estaba y se cambió está en
   *  los dos lados, y se va de los dos. */
  const sacarDeLoMarcado = (id: string) => {
    if (elegidos.has(id)) {
      setElegidos((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    }
    if (existentes.some((a) => a.id === id)) setQuitados((prev) => new Set(prev).add(id));
  };

  // ── Moodboards (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes). Cada carpeta es UN
  //    chip: el nuevo, o el que ya estaba en el destino si no se lo cambió ni se lo sacó.
  const chipDeLaCarpeta = (moodboardId: string): ChatAttachment | null => {
    const id = idDeCarpetaDeMoodboard(moodboardId);
    return elegidos.get(id)?.adjunto ?? (quitados.has(id) ? null : (existentes.find((a) => a.id === id) ?? null));
  };
  /** Las fotos marcadas de una carpeta pasan a ser su chip. Lo que ya estaba en el destino
   *  se reemplaza al confirmar: se quita el viejo y entra éste, con el mismo id. */
  const fijarCarpeta = (carpeta: Moodboard, fotos: MoodboardFoto[], marcadas: Set<string>) => {
    const id = idDeCarpetaDeMoodboard(carpeta.id);
    const chip = chipDeCarpeta(carpeta, fotos, marcadas);
    setElegidos((prev) => {
      const next = new Map(prev);
      if (chip) next.set(id, { adjunto: chip, pestana: 'moodboards' });
      else next.delete(id);
      return next;
    });
    if (existentes.some((a) => a.id === id)) setQuitados((prev) => new Set(prev).add(id));
  };
  /** La carpeta ENTERA desde su ícono, sin abrirla: el chip sin `fotoIds`. Una vacía no
   *  tiene qué elegir y se abre, para subirle fotos. */
  const elegirCarpetaEntera = (carpeta: Moodboard) => {
    if (carpeta.cantidadFotos === 0) {
      abrirCarpeta(carpeta);
      return;
    }
    const id = idDeCarpetaDeMoodboard(carpeta.id);
    setElegidos((prev) => new Map(prev).set(id, { adjunto: adjuntoDeCarpetaDeMoodboard(carpeta), pestana: 'moodboards' }));
    if (existentes.some((a) => a.id === id)) setQuitados((prev) => new Set(prev).add(id));
  };

  // Productos: la búsqueda también mira los datos de cada uno
  // (ADR-2026-10-21-la-busqueda-mira-los-datos-del-producto).
  const indiceDeBusqueda = useIndiceDeBusqueda(
    pestana === 'productos'
      ? fichas.flatMap((f) => (f.adjunto.kind === 'product' ? [f.adjunto.productId] : []))
      : [],
    pestana === 'productos' && busqueda.trim().length > 0,
  );

  if (!montado) return null;

  const actual = PESTANAS.find((p) => p.id === pestana) ?? PESTANAS[0];
  const filtro = busqueda.trim();
  // Lo que se lista: sin lo que ya está en el chat (es un chip en la caja, como en la
  // biblioteca), filtrado por nombre y, en Productos, en el orden elegido.
  const visibles = fichasVisibles(fichas, {
    pestana,
    // Con otro destino no hay nada que esconder: lo que está en el chat no está acá. Y
    // eligiendo varios tampoco: lo ya agregado se ve en la grilla, MARCADO.
    adjuntos: onAdjuntar || eligeVarios ? [] : chat.adjuntos,
    busqueda,
    orden,
    indice: indiceDeBusqueda,
  }).filter(
    // Con otro destino (la referencia de Editar clip) va UNA imagen, como la subida
    // (`image/*`): un video de Creaciones no sirve y fallaba recién al mandar.
    (f) => !onAdjuntar || f.adjunto.kind !== 'video',
  );
  const conNombres = tieneNombres(pestana);
  const quedaPorMostrar = dibujadas < visibles.length || hayMas;
  const llegarAlFinal = () => {
    if (dibujadas < visibles.length) setDibujadas((n) => n + PAGINA_DE_GRILLA);
    // `pedirMas` ya se frena solo mientras una página está en vuelo.
    else if (hayMas && !cargando) pedirMas();
  };
  // ── La pestaña Moodboards: dos niveles en la MISMA pestaña —las carpetas y, abierta una,
  //    sus fotos—, no una sub-vista (`vista`): la sub-vista esconde el pie, y desmarcar
  //    fotos sin ver lo elegido no sirve (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes).
  const fotosAbiertas = carpetaAbierta ? (fotosPorCarpeta.get(carpetaAbierta.id) ?? null) : null;
  const marcadasAbiertas =
    carpetaAbierta && fotosAbiertas ? fotosMarcadas(chipDeLaCarpeta(carpetaAbierta.id), fotosAbiertas) : new Set<string>();
  const todasMarcadas = fotosAbiertas ? estanTodas(marcadasAbiertas, fotosAbiertas) : false;
  const subiendoAca = subiendoAMoodboard && carpetaAbierta && subiendoAMoodboard.carpetaId === carpetaAbierta.id ? subiendoAMoodboard.quedan : 0;
  const pestanaDeMoodboards: PestanaDelSelector = {
    id: 'moodboards',
    label: deLaBiblioteca('moodboards').etiqueta,
    Icon: Palette,
    // Sin carpeta abierta, la grilla es la de Mi marca (`GrillaDeCarpetas`, en el
    // encabezado): "Nueva carpeta" primero, y cada carpeta con su ícono, sus fotos asomando y
    // el clip para elegirla entera. Abierta una, sus fotos, marcadas o no, con las que están
    // subiendo adelante (donde van a quedar).
    items: !carpetaAbierta
      ? []
      : [
          ...Array.from({ length: subiendoAca }, (_, i) => ({
            id: `subiendo-${i}`,
            nombre: 'Subiendo…',
            importando: true,
          })),
          ...(fotosAbiertas ?? []).map((f, i) => ({
            id: f.id,
            url: f.thumbUrl,
            nombre: `${carpetaAbierta.nombre} #${i + 1}`,
            seleccionado: marcadasAbiertas.has(f.id),
          })),
        ],
    cargando: !carpetaAbierta ? carpetas === null : fotosAbiertas === null,
    // Sin carpetas no hay nada que decir: la grilla ya muestra "Nueva carpeta".
    vacio: !carpetaAbierta ? '' : 'Esta carpeta todavía no tiene fotos.',
    aspecto: 'cuadrado',
    // Adentro de una carpeta, la tarjeta para subirle fotos, al frente como las altas de
    // las demás pestañas.
    acciones: carpetaAbierta
      ? [
          {
            id: 'agregar-fotos',
            label: subiendoAMoodboard ? `Subiendo ${subiendoAMoodboard.quedan}…` : 'Agregar fotos',
            sublabel: 'Referencias de estilo',
            Icon: ImagePlus,
            ocupado: subiendoAMoodboard !== null,
            onClick: () => {
              if (subiendoAMoodboard) return;
              setAviso(null);
              inputMoodboardRef.current?.click();
            },
          },
        ]
      : [],
    encabezado: !carpetaAbierta ? (
      carpetas && (
        <div className="pb-2">
          {/* Sin carpetas, qué sale de armarlas —el antes y después y los tres pasos— con la
              creación de la primera adentro. La grilla aparece con la primera carpeta. */}
          {carpetas.length === 0 ? (
            <VacioDeEstilos onCrear={crearCarpeta} />
          ) : (
          <GrillaDeCarpetas
            carpetas={carpetas}
            grillaClassName="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 md:grid-cols-5"
            onAbrir={abrirCarpeta}
            onCrear={crearCarpeta}
            onRenombrar={renombrarCarpeta}
            onCambiarColor={pintarCarpeta}
            onEliminar={eliminarCarpeta}
            onAdjuntar={elegirCarpetaEntera}
            adjuntada={(id) => chipDeLaCarpeta(id) !== null}
            rotuloAdjuntada={(c) => {
              const chip = chipDeLaCarpeta(c.id);
              return chip?.kind === 'moodboard' && chip.fotoIds ? fotosDelMoodboard(chip) : 'Elegida';
            }}
            tituloAdjuntar="Elegir la carpeta entera"
          />
          )}
        </div>
      )
    ) : (
      <div className="flex flex-wrap items-center gap-2 pb-4">
        <button
          type="button"
          onClick={() => setCarpetaAbierta(null)}
          className="inline-flex items-center gap-1 rounded-full py-1.5 pl-2 pr-3 text-[13px] font-semibold text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.095)]"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
          {deLaBiblioteca('moodboards').etiqueta}
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-brand-black">{carpetaAbierta.nombre}</p>
          {fotosAbiertas && fotosAbiertas.length > 0 && (
            <p className="text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">
              {marcadasAbiertas.size} de {fotosAbiertas.length} elegidas
            </p>
          )}
        </div>
        {/* La carpeta entera de un toque, y después se desmarca lo que no va. */}
        {fotosAbiertas && fotosAbiertas.length > 0 && (
          <button
            type="button"
            onClick={() => fijarCarpeta(carpetaAbierta, fotosAbiertas, alternarTodas(marcadasAbiertas, fotosAbiertas))}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full py-1.5 px-3 text-[12.5px] font-semibold transition-colors ${
              todasMarcadas
                ? 'bg-neutral-100 text-[rgb(var(--brand-black-rgb)_/_0.9)] hover:bg-neutral-200'
                : 'bg-brand-primary text-white hover:bg-brand-primary-hover'
            }`}
          >
            <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
            {todasMarcadas ? 'Quitar todas' : 'Elegir toda la carpeta'}
          </button>
        )}
      </div>
    ),
    // Sólo hay items adentro de una carpeta: las carpetas las dibuja la grilla de arriba.
    onElegir: (item) => {
      setAviso(null);
      if (!carpetaAbierta || !fotosAbiertas) return;
      fijarCarpeta(carpetaAbierta, fotosAbiertas, alternarFoto(marcadasAbiertas, item.id));
    },
  };

  // ── La pestaña Elementos de marca (ADR-2026-10-19-elementos-de-marca): los dos niveles de
  //    Estilos, pero adentro de una carpeta se elige cada elemento por separado. Una
  //    tipografía se elige como su HOJA DE MUESTRA, que se dibuja en el navegador
  //    (ADR-2026-10-19-la-tipografia-viaja-como-hoja-de-muestra).
  const elementosAbiertos = carpetaDeElementos ? (elementosPorCarpeta.get(carpetaDeElementos.id) ?? null) : null;
  const idDelAdjunto = (el: ElementoDeMarca) => (el.tipo === 'font' ? idDeTipografia(el.id) : idDeElementoDeMarca(el.id));
  const subiendoAElementosAca =
    subiendoElementos && carpetaDeElementos && subiendoElementos.carpetaId === carpetaDeElementos.id ? subiendoElementos.quedan : 0;
  const pestanaDeElementos: PestanaDelSelector = {
    id: 'elementos',
    label: deLaBiblioteca('elementos').etiqueta,
    Icon: Shapes,
    items: !carpetaDeElementos
      ? []
      : [
          ...Array.from({ length: subiendoAElementosAca }, (_, i) => ({
            id: `subiendo-elemento-${i}`,
            nombre: 'Subiendo…',
            importando: true,
          })),
          ...(elementosAbiertos ?? []).map((el) => ({
            id: el.id,
            // La tipografía se ve con su muestra chica; mientras se dibuja, el tile en gris.
            url: el.tipo === 'font' ? previasDeFuentes.get(el.id) : el.url,
            nombre: el.titulo,
            seleccionado: estaMarcado(idDelAdjunto(el)),
            importando: preparandoFuente === el.id,
          })),
        ],
    cargando: !carpetaDeElementos ? carpetasDeElementos === null : elementosAbiertos === null,
    vacio: !carpetaDeElementos
      ? ''
      : 'Esta carpeta todavía no tiene elementos.',
    aspecto: 'cuadrado',
    encuadre: 'entera',
    // Cada elemento con su título debajo (ADR-2026-10-20-los-elementos-de-marca-tienen-titulo).
    conNombres: true,
    acciones: carpetaDeElementos
      ? [
          {
            id: 'agregar-elementos',
            label: subiendoElementos ? `Subiendo ${subiendoElementos.quedan}…` : 'Agregar archivos',
            sublabel: 'Logos, gráficos, tipografías',
            Icon: ImagePlus,
            ocupado: subiendoElementos !== null,
            onClick: () => {
              if (subiendoElementos) return;
              setAviso(null);
              inputElementosRef.current?.click();
            },
          },
        ]
      : [],
    encabezado: !carpetaDeElementos ? (
      carpetasDeElementos &&
      (!brandId ? (
        <p className="py-8 text-center text-[13.5px] text-[rgb(var(--brand-black-rgb)_/_0.6)]">Elegí una marca para ver sus elementos.</p>
      ) : (
        <div className="pb-2">
          {carpetasDeElementos.length === 0 ? (
            <VacioDeElementos
              onCrearSugeridas={async () => {
                try {
                  for (const c of [...CARPETAS_SUGERIDAS].reverse()) await nuevaCarpetaDeElementos(c.nombre, c.color);
                } catch (e) {
                  avisarMoodboard(e, 'crear las carpetas');
                }
              }}
              onCrear={async (nombre) => {
                try {
                  const carpeta = await nuevaCarpetaDeElementos(nombre);
                  if (carpeta) setCarpetaDeElementos(carpeta);
                } catch (e) {
                  avisarMoodboard(e, 'crear la carpeta');
                }
              }}
            />
          ) : (
            <GrillaDeCarpetas
              carpetas={carpetasDeElementos}
              unidad={UNIDAD_ELEMENTOS}
              grillaClassName="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 md:grid-cols-5"
              onAbrir={abrirCarpetaDeElementos}
              onCrear={async (nombre) => {
                try {
                  await nuevaCarpetaDeElementos(nombre);
                } catch (e) {
                  avisarMoodboard(e, 'crear la carpeta');
                }
              }}
              onRenombrar={async (id, nombre) => {
                const antes = carpetasDeElementos;
                setCarpetasDeElementos((prev) => prev && prev.map((c) => (c.id === id ? { ...c, nombre } : c)));
                try {
                  await renombrarCarpetaDeElementos(id, nombre);
                } catch (e) {
                  setCarpetasDeElementos(antes);
                  avisarMoodboard(e, 'guardar el nombre');
                }
              }}
              onCambiarColor={async (id, color) => {
                const antes = carpetasDeElementos;
                setCarpetasDeElementos((prev) => prev && prev.map((c) => (c.id === id ? { ...c, color } : c)));
                try {
                  await pintarCarpetaDeElementos(id, color);
                } catch (e) {
                  setCarpetasDeElementos(antes);
                  avisarMoodboard(e, 'guardar el color');
                }
              }}
              onEliminar={async (id) => {
                try {
                  const borrados = elementosPorCarpeta.get(id) ?? [];
                  await eliminarCarpetaDeElementos({ carpetaId: id });
                  setCarpetasDeElementos((prev) => prev && prev.filter((c) => c.id !== id));
                  // Lo elegido de esa carpeta ya no existe.
                  for (const el of borrados) sacarDeLoMarcado(idDelAdjunto(el));
                } catch (e) {
                  avisarMoodboard(e, 'eliminar la carpeta');
                }
              }}
            />
          )}
        </div>
      ))
    ) : (
      <div className="flex flex-wrap items-center gap-2 pb-4">
        <button
          type="button"
          onClick={() => setCarpetaDeElementos(null)}
          className="inline-flex items-center gap-1 rounded-full py-1.5 pl-2 pr-3 text-[13px] font-semibold text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.095)]"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
          {deLaBiblioteca('elementos').etiqueta}
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-brand-black">{carpetaDeElementos.nombre}</p>
        </div>
      </div>
    ),
    onElegir: (item) => {
      setAviso(null);
      const el = (elementosAbiertos ?? []).find((x) => x.id === item.id);
      if (el) void (el.tipo === 'font' ? alternarTipografia(el) : alternarElemento(el));
    },
  };

  const pestanas: PestanaDelSelector[] = PESTANAS.filter((p) => seOfrece(p.id)).map((p) =>
    p.id === 'moodboards'
      ? pestanaDeMoodboards
      : p.id === 'elementos'
        ? pestanaDeElementos
        : p.id === 'pinterest'
          ? pinterest.armar(estaMarcado, (adjunto) => alternar(adjunto, 'pinterest'))
          : ({
    id: p.id,
    label: p.etiqueta ?? deLaBiblioteca(p.id).etiqueta,
    Icon: p.Icon,
    // Sólo la pestaña abierta tiene items: el hook carga una por vez, y las demás no se
    // dibujan igual (el pop-up muestra la activa).
    items:
      p.id === pestana
        ? visibles.slice(0, dibujadas).map((f) => ({
            id: f.clave,
            url: f.imageUrl,
            // Creaciones y subidas no tienen nombre: el `title` del tile cae en el de la
            // pestaña, que es lo único cierto que se puede decir de la miniatura.
            nombre: f.nombre ?? deLaBiblioteca(p.id).etiqueta,
            sku: f.sku ?? null,
            ...(eligeVarios ? { seleccionado: estaMarcado(f.adjunto.id) } : {}),
          }))
        : [],
    cargando: p.id === pestana ? cargando : false,
    // El vacío dice por qué: nada con ese nombre, todo ya en el chat, o la fuente vacía.
    vacio:
      p.id === pestana && filtro && conNombres
        ? 'Nada con ese nombre.'
        : p.id === pestana && fichas.length > 0 && !onAdjuntar && !eligeVarios
          ? 'Ya está todo en el chat.'
          : deLaBiblioteca(p.id).vacio,
    aspecto: p.retrato ? ('retrato' as const) : ('cuadrado' as const),
    conNombres: p.id === pestana && conNombres,
    // Las tarjetas de ALTA van al frente de la grilla de SU fuente: son el alta de esa
    // fuente, no una fuente más, y por eso no son pestañas. Qué alta tiene cada una y
    // cómo se llama sale de `ALTAS_POR_PESTANA`, la tabla de la biblioteca.
    // Con las dos tarjetas de subida, Productos no lleva la tarjeta de alta: las tarjetas
    // van arriba de la grilla (`encabezado`).
    encabezado:
      subidaEnDosTarjetas && p.id === 'productos' ? (
        <div className="grid grid-cols-2 gap-3 pb-4">
          {(['uno', 'varios'] as const).map((variante) => (
            <TarjetaDeSubidaEnGrilla
              key={variante}
              variante={variante}
              onClick={() => {
                setAviso(null);
                setSubirEnVentana(variante);
              }}
              aspecto="aspect-square md:aspect-[5/2]"
              redondeo="rounded-2xl"
            />
          ))}
        </div>
      ) : undefined,
    acciones: ALTAS_POR_PESTANA[p.id]
      .filter((a) => !(subidaEnDosTarjetas && a.id === 'subir-producto'))
      .map((a) => ({
      id: a.id,
      // Mientras el backend procesa la pose, la tarjeta lo dice y no se puede volver a
      // tocar: es una sola por vez y tarda (lo mismo hace la biblioteca).
      label:
        a.id === 'subir-pose' && subiendoPose
          ? 'Procesando pose…'
          : // Acá sube cualquier archivo, no sólo imágenes (ver `PESTANAS`). La biblioteca
            // lateral sigue diciendo "Subir imagen": su input es sólo de fotos.
            a.id === 'subir-imagen'
            ? 'Subir archivo'
            : a.etiqueta,
      Icon: ICONO_DE_ALTA[a.id].Icon,
      tono: ICONO_DE_ALTA[a.id].tono,
      dibujo: ICONO_DE_ALTA[a.id].dibujo,
      // El renglón chico de la tarjeta ("Fotos y nombre"), el mismo de la biblioteca.
      sublabel: a.detalle,
      // La tarjeta muestra el spinner mientras sube el archivo o se procesa la pose.
      ocupado:
        (a.id === 'subir-pose' && subiendoPose) || (a.id === 'subir-imagen' && subiendo),
      onClick: () => {
        setAviso(null);
        // "Subir imagen" no tiene pantalla: es el file picker, como en la biblioteca.
        if (a.id === 'subir-imagen') {
          inputRef.current?.click();
          return;
        }
        if (a.id === 'subir-pose' && subiendoPose) return;
        setAlta(a.id);
      },
    })),
    onElegir: (item) => {
      const ficha = fichas.find((f) => f.clave === item.id);
      // (El `nombre` del item ya salió de la ficha; acá se vuelve a buscar porque el
      // pop-up devuelve el item que dibujó, no la ficha.)
      if (!ficha) return;
      if (!eligeVarios) {
        if (enganchar(ficha.adjunto)) cerrar();
        return;
      }
      // Selección múltiple: tocar marca o desmarca; se confirma con el botón del pie.
      setAviso(null);
      alternar(ficha.adjunto, p.id);
    },
  }));

  // ── El pie de la selección múltiple (ADR-2026-10-05-el-pie-del-selector-muestra-lo-elegido).
  //    En escritorio: las miniaturas de lo marcado (lo que ya estaba + lo nuevo), cada una
  //    con su X, y a la derecha el botón. En el teléfono: el conteo, "Limpiar" y el botón a
  //    todo el ancho. El botón dice qué se AGREGA; si no hay nada nuevo, "Listo".
  const nuevos = [...elegidos.values()];
  const conservados = existentes.filter((a) => !quitados.has(a.id));
  const miniaturas: MiniaturaElegida[] = [...conservados, ...nuevos.map((n) => n.adjunto)].map((a) => ({
    id: a.id,
    url: miniaturaDeAdjunto(a) ?? null,
    nombre: a.kind === 'image' ? 'Imagen' : 'name' in a && a.name ? a.name : 'Adjunto',
  }));
  const confirmar = () => {
    // Primero lo desmarcado, después lo nuevo de a uno y en orden: si el chat llegó a su
    // tope de archivos, se corta ahí; lo que entró queda agregado y lo que no sigue marcado.
    for (const id of quitados) quitarDelDestino(id);
    setQuitados(new Set());
    const pendientes = new Map(elegidos);
    for (const [id, e] of elegidos) {
      if (!enganchar(e.adjunto)) break;
      pendientes.delete(id);
    }
    if (pendientes.size === 0) cerrar();
    else setElegidos(pendientes);
  };
  const pie =
    eligeVarios && (miniaturas.length > 0 || quitados.size > 0) ? (
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <MiniaturasElegidas
          items={miniaturas}
          onQuitar={sacarDeLoMarcado}
          className="hidden flex-1 sm:flex"
        />
        <div className="flex items-center justify-between gap-3 sm:hidden">
          <span className="text-[13px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.6)]">
            {miniaturas.length} {miniaturas.length === 1 ? 'elegido' : 'elegidos'}
          </span>
          {miniaturas.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setElegidos(new Map());
                setQuitados(new Set(existentes.map((a) => a.id)));
              }}
              className="rounded-full py-1 px-2.5 text-[13px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.55)] transition-colors hover:bg-black/[0.05] hover:text-brand-black"
            >
              Limpiar
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={confirmar}
          className="inline-flex w-full shrink-0 items-center justify-center rounded-full bg-brand-primary py-3 px-6 text-[14.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover sm:ml-auto sm:w-auto sm:py-2.5 sm:px-5"
        >
          {nuevos.length > 0 ? rotuloDeAgregar(nuevos.map((n) => n.pestana), palabraDeModelo) : 'Listo'}
        </button>
      </div>
    ) : null;

  // Las acciones del pop-up ENTERO: ninguna. Acá cada tarjeta de alta pertenece a su
  // fuente y viaja en la pestaña (`acciones` de cada una); la prop global la usa el
  // Estudio de video, donde las acciones valen para el pop-up y no para una fuente.
  const acciones: AccionDelSelector[] = [];

  return (
    <>
      {pantallaDePreparar}
      <input
        ref={inputRef}
        type="file"
        /* Lo mismo que el "+" de la caja (`ACEPTA_ADJUNTOS`): imágenes, videos, PDF,
           planillas y textos. Antes eran sólo imágenes porque lo demás iba por el menú
           del "+", que ya no se monta en ninguna caja. `prepararAdjunto` los maneja a
           todos. Con otro destino (la referencia de "Editar clip"), una imagen. */
        accept={onAdjuntar ? 'image/*' : ACEPTA_ADJUNTOS}
        multiple={eligeVarios}
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const archivos = Array.from(e.target.files ?? []);
          // Se limpia el valor para que elegir el MISMO archivo dos veces vuelva a
          // disparar el change.
          e.target.value = '';
          void subirImagenes(archivos);
        }}
      />
      {/* "Agregar fotos" adentro de una carpeta de Moodboards: sólo imágenes, como en Mi
          marca (una referencia de estilo es una foto). */}
      <input
        ref={inputMoodboardRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const archivos = Array.from(e.target.files ?? []).filter(
            (f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name),
          );
          e.target.value = '';
          if (carpetaAbierta) void subirFotosACarpeta(carpetaAbierta, archivos);
        }}
      />
      {/* "Agregar archivos" adentro de una carpeta de Elementos de marca: imágenes y
          tipografías, como en Mi marca. */}
      <input
        ref={inputElementosRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,.heic,.heif,.woff2,.woff,.ttf,.otf,.zip"
        multiple
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const archivos = Array.from(e.target.files ?? []).filter(
            (f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name) || esArchivoDeFuente(f) || esZip(f),
          );
          e.target.value = '';
          if (!carpetaDeElementos) return;
          carpetaAlElegirRef.current = carpetaDeElementos;
          void prepararElementos(archivos);
        }}
      />
      <SelectorDeImagenModal
        titulo="Elegí qué agregar"
        pestanas={pestanas}
        // Las tarjetas de alta, los nombres y las columnas de la biblioteca del Agente:
        // 2 en el teléfono y 5 en escritorio
        // (ADR-2026-09-25-el-selector-de-fuentes-toma-la-biblioteca).
        estiloDeAccion="tarjeta"
        claseDeGrilla="grid-cols-2 md:grid-cols-5"
        // En el teléfono la hoja llega hasta arriba
        // (ADR-2026-09-25-agregar-con-rotulo-y-la-hoja-entera-en-el-telefono).
        altoEnElTelefono="completa"
        visible={visible}
        quedaPorMostrar={pestana === 'pinterest' ? pinterest.quedaPorMostrar : quedaPorMostrar}
        onLlegarAlFinal={pestana === 'pinterest' ? pinterest.llegarAlFinal : llegarAlFinal}
        // Cargando, la tarjeta de alta ("Subir archivo") queda a la vista y el resto de la
        // grilla late; cada miniatura late hasta que baja
        // (ADR-2026-09-27-el-selector-de-fuentes-carga-con-esqueleto).
        conEsqueleto
        pie={pie}
        herramientas={
          conNombres ? (
            // `items-stretch`: "Ordenar por" mide lo mismo que el buscador (lo pide
            // `altoDeLaFila`), que es el que pone el alto.
            <div className="flex items-stretch gap-2">
              {/* El buscador de la biblioteca, tal cual: gris, redondeado, con la lupa. */}
              <label className="flex min-w-0 flex-1 items-center gap-2 rounded-xl bg-[#F0F2F7] px-3 py-2">
                <Search className="h-4 w-4 shrink-0 text-[rgb(var(--brand-black-rgb)_/_0.4)]" aria-hidden="true" />
                <input
                  type="search"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder={`Buscar en ${deLaBiblioteca(actual.id).etiqueta.toLowerCase()}`}
                  aria-label={`Buscar en ${deLaBiblioteca(actual.id).etiqueta.toLowerCase()}`}
                  // 16px: por debajo, iOS zoomea la pantalla al enfocar el input.
                  className="min-w-0 flex-1 bg-transparent text-[16px] text-brand-black outline-none placeholder:text-[rgb(var(--brand-black-rgb)_/_0.38)] sm:text-[13.5px]"
                />
              </label>
              {/* "Ordenar por", sólo en Productos: el mismo botón y el mismo orden que los
                  pickers de productos de los modos. */}
              {pestana === 'productos' && <SelectorDeOrden sort={orden} onSortChange={setOrden} altoDeLaFila />}
            </div>
          ) : null
        }
        pestanaActiva={actual.id}
        onCambiarPestana={(id) => {
          setAviso(null);
          setPestana(id as PestanaBiblioteca);
        }}
        acciones={acciones}
        aviso={aviso}
        vista={
          alta === null
            ? null
            : alta === 'varios'
              ? {
                  // Se llegó desde el alta de producto: la flecha devuelve ahí.
                  volverA: 'Subir producto',
                  onVolver: () => {
                    if (variosOcupado) return;
                    setAlta('subir-producto');
                  },
                  contenido: (
                    // Como el resto de las altas, usa la ventana entera en dos columnas
                    // (ADR-2026-09-24-el-alta-en-bloque-tambien-en-dos-columnas). Antes
                    // iba en una columna centrada de 640 —la medida de su ventana propia—
                    // y la lista de productos quedaba apretada contra el pie.
                    <div className="h-full w-full">
                      <AgregarVariosModal
                        embebido
                        open
                        companyId={companyId}
                        brandId={brandId}
                        userPlan={plan}
                        // Cancelar vuelve al alta de a uno. Si ya guardó, la vista se fue
                        // a la grilla y este `onClose` —que llega después— no la pisa.
                        onClose={() =>
                          setAlta((v) => (v === 'varios' ? 'subir-producto' : v))
                        }
                        onSaved={() => {
                          refrescarProductos();
                          setAlta(null);
                        }}
                        // Por encima de esta ventana (z-120): es el aviso de Tiendanube,
                        // que sí abre aparte.
                        zIndex={130}
                        onOcupado={setVariosOcupado}
                      />
                    </div>
                  ),
                }
              : {
                  // La cabecera es el camino de vuelta —la fuente, como la nombra la
                  // biblioteca—; el título de lo que se está haciendo lo pone el alta.
                  volverA: deLaBiblioteca(pestana).etiqueta,
                  onVolver: () => setAlta(null),
                  contenido: (
                    // El alta usa la VENTANA ENTERA (980), en dos columnas: lo que se
                    // hace a la izquierda, el ejemplo y los tips a la derecha
                    // (ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-columnas).
                    // Antes iba en una columna centrada de 640 —la medida para la que
                    // están hechas en el sheet y en el panel lateral— y sobraban ~170 px
                    // de blanco a cada lado, con el CTA abajo del pliegue.
                    <div className="flex h-full w-full flex-col">
                      <AltaDeBiblioteca
                        ancho
                        alta={alta}
                        usuarioId={usuarioId}
                        companyId={companyId}
                        brandId={brandId}
                        plan={plan}
                        rubro={rubro}
                        subiendoPose={subiendoPose}
                        // El "Volver" lo pone la cabecera de esta ventana.
                        sinVolver
                        onVolver={() => setAlta(null)}
                        onProductosRefrescados={refrescarProductos}
                        onModelosRefrescados={refrescarModelos}
                        // Un modelo o un fondo recién hechos se ENGANCHAN y el pop-up se
                        // cierra: es lo que hacen el "Utilizar" del picker de modelos y
                        // el "Confirmar" del de fondos, y lo que hace este pop-up con
                        // cualquier miniatura que se toca.
                        onModeloListo={(modelo) => {
                          if (enganchar(adjuntoDeModelo(modelo))) cerrar();
                        }}
                        onFondosRefrescados={() => void refrescarFondosGuardados()}
                        onFondoListo={(fondo) => {
                          if (enganchar(adjuntoDeFondoGuardado(fondo))) cerrar();
                        }}
                        // La pose se procesa afuera del alta (tarda): se vuelve a la
                        // grilla y la tarjeta avisa.
                        onFotoDePose={(archivo) => void subirPose(archivo)}
                        // Sólo la mira el alta de producto: en modo ancho "Subir varios"
                        // vive en la barra de abajo.
                        onSubirVarios={() => setAlta('varios')}
                      />
                    </div>
                  ),
                }
        }
        onCerrar={cerrar}
      />
      {subidaEnDosTarjetas && (
        // Va por portal en z-210, encima de esta ventana (z-120). Lo nuevo aparece en la
        // grilla de Productos al refrescar, para marcarlo desde ahí.
        <ModalDeSubirProductos
          modo={subirEnVentana}
          onCerrar={() => setSubirEnVentana(null)}
          companyId={companyId}
          brandId={brandId}
          userPlan={plan}
          onProductosNuevos={refrescarProductos}
        />
      )}
    </>
  );
};

export default ModalDeFuentes;
